import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { ReactComponent as QRIcon } from '../../../../assets/icons/action-qr.svg';
import { ReactComponent as X } from '../../../../assets/icons/close-icon.svg';
import { ReactComponent as LinkIcon } from '../../../../assets/icons/link.svg';
import { ReactComponent as MapPin } from '../../../../assets/icons/location-icon.svg';
import { ReactComponent as Sync } from '../../../../assets/icons/sync.svg';
import { ReactComponent as Upload } from '../../../../assets/icons/upload-icon.svg';
import { ReactComponent as Zap } from '../../../../assets/icons/zap.svg';
import DefaultApiLimit from '../../../../config/defaultLimit';
import { ApiRequest } from '../../../../interface/common.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import {
  broadcastCpoLocations,
  fetchCpoLocationFilters,
  fetchCpoLocations,
  setSortData,
  syncCpoLocations,
  updateCPOLocationsFilters,
} from '../../../../redux/slices/OCPI/CPOLocationsSlice';
import { RootState } from '../../../../redux/store';
import MyButton from '../../../../shared-components/button/Button';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
// import TabMenus from '../../../../shared-components/tab-menus/TabMenus';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';
import { getLocalStorageItem } from '../../../../shared-functions/LocalStorage';
import chargingLocationAPI from '../../../../utils/api/charging-location';
import qrApi from '../../../../utils/api/qr';
import DeviceQrCode from '../../../charger-pages/charger-management/components/DeviceQrCode';

// const syncModeTabs = ['AUTOMATIC', 'MANUAL'] as const;

/**
 * Builds a user-facing error message for a failed charging-station sync request,
 * distinguishing between being offline, no response from the server, and a server error.
 * @param {any} error - The error thrown by the sync request.
 * @param {any} t - Translation function.
 * @returns {string} A user-facing error message.
 */
const resolveSyncDataErrorMessage = (error: any, t: any): string => {
  if (typeof window !== 'undefined' && !window.navigator.onLine) {
    return t('offlineErrorMessage');
  }
  if (error?.response) {
    return (
      error.response.data?.message ||
      error.response.data?.status_message ||
      t('failedToSyncChargingStationData')
    );
  }
  if (error?.request) {
    return t('noResponseFromServer');
  }
  return error?.message || t('failedToSyncChargingStationData');
};

// ── Types ─────────────────────────────────────────────────────────────────────

interface OcpiEvse {
  uid: string;
  evse_id?: string;
  status: string;
  connectors?: any[];
  last_updated?: string;
}

interface OcpiLocation {
  id: string;
  name?: string;
  address?: string;
  city?: string;
  country?: string;
  country_code?: string;
  party_id?: string;
  publish?: boolean;
  coordinates?: { latitude: string; longitude: string };
  latitude?: number;
  longitude?: number;
  evses?: OcpiEvse[];
  last_updated?: string;
  postal_code?: string;
  parking_type?: string;
  time_zone?: string;
  opening_times?: { twentyfourseven?: boolean; regular_hours?: any[] };
  _objectId?: string;
  _lastUpdated?: string;
  _createdAt?: string;
  createdAt?: string;
  created_at?: string;
  created?: string;
}

// ── Sub-components ─────────────────────────────────────────────────────────────

/**
 * Renders a status badge for an EVSE.
 * @param {object} properties - Component properties.
 * @param {string} properties.status - The status.
 * @returns {any} The rendered status badge.
 */
function StatusBadge({ status }: { status: string }): JSX.Element {
  const { t } = useTranslation('ocpi');
  let modifier = 'cpo-status-badge--pending';
  let label = status;
  if (status === 'AVAILABLE') {
    modifier = 'cpo-status-badge--available';
    label = t('available');
  } else if (status === 'CHARGING') {
    modifier = 'cpo-status-badge--charging';
    label = t('charging');
  } else if (status === 'BLOCKED') {
    modifier = 'cpo-status-badge--blocked';
    label = t('blocked');
  } else if (status === 'FAULTED') {
    modifier = 'cpo-status-badge--blocked';
    label = t('faulted');
  } else if (status === 'UNKNOWN') {
    label = t('unknown');
  }

  return <span className={`cpo-status-badge ${modifier}`}>{label}</span>;
}

// ── EVSE Row Component ────────────────────────────────────────────────────────

/**
 * Renders an individual EVSE row with dynamically fetched URL.
 * @param {object} properties - Component properties.
 * @param {OcpiEvse} properties.evse - The EVSE data.
 * @param {boolean} properties.isOperationsPath - Whether the current path is operations.
 * @param {Function} properties.handleOpenQrCode - Callback to open QR code modal.
 * @returns {JSX.Element} The rendered EVSE row.
 */
function CpoEvseRow({
  evse,
  isOperationsPath,
  proxyUrl,
  handleOpenQrCode,
}: {
  evse: OcpiEvse;
  isOperationsPath: boolean;
  proxyUrl?: string;
  handleOpenQrCode: (evseId: string) => void;
}): JSX.Element {
  const { t } = useTranslation(['ocpi', 'partnerDeviceManagement']);
  const dispatch = useAppDispatch();

  const evseIdentifier = evse.evse_id || evse.uid;
  const connectorId = evse.connectors?.[0]?.id || '1';
  const formattedDeviceId = evseIdentifier.includes('_')
    ? evseIdentifier
    : `${evseIdentifier}_${connectorId}`;

  /**
   * Handles copying the proxy URL to the clipboard.
   * @param {React.MouseEvent} event - The mouse event.
   */
  const handleCopyUrl = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (proxyUrl) {
      navigator.clipboard.writeText(proxyUrl);
      dispatch(
        setNotificationMessage({
          type: 'success',
          notificationMessage: t('partnerDeviceManagement:urlCopiedToClipboard'),
        }),
      );
    }
  };

  return (
    <div className="cpo-evse-row">
      <div className="cpo-evse-content">
        <div className="cpo-evse-head">
          <span className="cpo-evse-id">{evseIdentifier}</span>
          <StatusBadge status={evse.status || 'UNKNOWN'} />
        </div>
        <div className="cpo-evse-sub">
          {evse.connectors?.length || 0}{' '}
          {t(
            (evse.connectors?.length || 0) !== 1
              ? 'connectorCountPlural'
              : 'connectorCountSingular',
          )}
          {evse.connectors?.[0]?.standard ? ` · ${evse.connectors[0].standard}` : ''}
          {evse.connectors?.[0]?.power_type ? ` · ${evse.connectors[0].power_type}` : ''}
        </div>
        {proxyUrl && !isOperationsPath && (
          <div className="cpo-evse-url-wrap">
            <button
              type="button"
              className="cpo-link-icon-circle"
              onClick={handleCopyUrl}
              title={t('partnerDeviceManagement:copyUrl')}>
              <LinkIcon width={12} height={12} />
            </button>
            <a
              href={proxyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="cpo-evse-link"
              title={proxyUrl}>
              {proxyUrl}
            </a>
          </div>
        )}
      </div>
      {!isOperationsPath && evseIdentifier && (
        <button
          type="button"
          className="cpo-evse-qr-btn"
          title={t('partnerDeviceManagement:generateQrCode')}
          onClick={(event) => {
            event.stopPropagation();
            handleOpenQrCode(formattedDeviceId);
          }}>
          <QRIcon />
        </button>
      )}
    </div>
  );
}

CpoEvseRow.defaultProps = {
  proxyUrl: '',
};

// ── EVSE Drill-Down Modal ─────────────────────────────────────────────────────

/**
 * Renders the EVSE drill-down modal.
 * @param {object} properties - Component properties.
 * @param {OcpiLocation} properties.location - The location details.
 * @param {Function} properties.onClose - The close callback.
 * @returns {any} The rendered EVSE drill-down modal.
 */
function LocationDrillDownModal({
  location,
  onClose,
}: Readonly<{
  location: OcpiLocation;
  onClose: () => void;
}>): JSX.Element {
  const { t } = useTranslation(['ocpi', 'partnerDeviceManagement']);
  const currentPath = useLocation();
  const isOperationsPath = currentPath.pathname.includes('operations');
  const evses: OcpiEvse[] = location.evses || [];
  const [qrEvseId, setQrEvseId] = useState<string | null>(null);
  const [qrCodeMap, setQrCodeMap] = useState<Record<string, string>>({});

  useEffect(() => {
    let isMounted = true;
    if (evses.length > 0 && !isOperationsPath) {
      const evseIds = evses.map((evse) => {
        const evseIdentifier = evse.evse_id || evse.uid;
        const connectorId = evse.connectors?.[0]?.id || '1';
        return evseIdentifier.includes('_') ? evseIdentifier : `${evseIdentifier}_${connectorId}`;
      });

      qrApi
        .getProxyUrlsByEvseIds(evseIds)
        .then((response) => {
          if (isMounted && response.data?.data) {
            const newQrCodeMap: Record<string, string> = {};
            response.data.data.forEach((qrData: any) => {
              if (qrData.evseId && qrData.proxyUrl) {
                newQrCodeMap[qrData.evseId] = qrData.proxyUrl;
              }
            });
            setQrCodeMap(newQrCodeMap);
          }
        })
        .catch(() => {
          // Silent catch for bulk fetch
        });
    }
    return () => {
      isMounted = false;
    };
  }, [evses, isOperationsPath]);

  const { tenants, selectedRole } = useAppSelector((state: RootState) => state.ocpiConnections);

  const matchedTenant =
    tenants?.find(
      (tenant: any) =>
        location.party_id && tenant.partyId?.toUpperCase() === location.party_id?.toUpperCase(),
    ) || selectedRole;

  const cpoDomain =
    matchedTenant?.businessDetails?.domain || getLocalStorageItem('client-name') || '';

  /**
   * Handles opening the QR code modal for an EVSE.
   * @param {string} evseId - The EVSE identifier.
   */
  const handleOpenQrCode = (evseId: string) => {
    setQrEvseId(evseId);
  };

  /**
   * Handles closing the QR code modal.
   */
  const handleCloseQrCode = () => {
    setQrEvseId(null);
  };

  return (
    <>
      <ModalBox
        open
        header={false}
        notification
        title={null}
        closeModal={onClose}
        content={
          <div className="cpo-drilldown-modal">
            {/* Header */}
            <div className="cpo-drilldown-header">
              <div>
                <h3 className="cpo-drilldown-title">{location.name || t('location')}</h3>
                <p className="cpo-drilldown-subtitle">
                  <MapPin width={12} height={12} />
                  {location.address}
                  {location.city ? `, ${location.city}` : ''}
                </p>
              </div>
              <button type="button" onClick={onClose} className="cpo-drilldown-close">
                <X height={30} width={30} />
              </button>
            </div>

            {/* EVSE List */}
            <div className="cpo-drilldown-body">
              {evses.length > 0 && (
                <div className="cpo-drilldown-select-all-row">
                  <span className="cpo-drilldown-select-all-count">
                    {evses.length} {t(evses.length !== 1 ? 'evseCountPlural' : 'evseCountSingular')}
                  </span>
                </div>
              )}

              {evses.length === 0 ? (
                <div className="cpo-drilldown-empty">
                  <Zap height={32} width={32} className="cpo-drilldown-empty-icon" />
                  <p>{t('noEvsesFoundForLocation')}</p>
                  <p>{t('syncFromCpoToPopulateChargers')}</p>
                </div>
              ) : (
                <div className="cpo-evse-list">
                  {evses.map((evse) => {
                    const evseIdentifier = evse.evse_id || evse.uid;
                    const connectorId = evse.connectors?.[0]?.id || '1';
                    const formattedDeviceId = evseIdentifier.includes('_')
                      ? evseIdentifier
                      : `${evseIdentifier}_${connectorId}`;

                    return (
                      <CpoEvseRow
                        key={evse.uid}
                        evse={evse}
                        isOperationsPath={isOperationsPath}
                        proxyUrl={qrCodeMap[formattedDeviceId]}
                        handleOpenQrCode={handleOpenQrCode}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        }
      />

      {qrEvseId && (
        <ModalBox
          open={Boolean(qrEvseId)}
          header
          title={t('adhocQrCode')}
          closeModal={handleCloseQrCode}
          notification={false}
          content={
            <DeviceQrCode
              deviceSN={qrEvseId}
              handleClose={handleCloseQrCode}
              isRoaming
              cpoDomain={cpoDomain}
              proxyUrl={qrCodeMap[qrEvseId]}
            />
          }
        />
      )}
    </>
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────

/**
 * CpoLocationsPanel component for managing CPO OCPI locations.
 * @param {object} properties - Component properties.
 * @param {string} properties.countryCode - The country code.
 * @param {string} properties.partyId - The party ID.
 * @returns {any} The rendered component.
 */
export default function CpoLocationsPanel({
  countryCode,
  partyId,
}: Readonly<{
  countryCode: string;
  partyId: string;
}>): JSX.Element {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['ocpi']);
  const {
    locations,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    sortBy,
    sortField,
    filters,
    filterListLoading,
  } = useAppSelector((state: RootState) => state.cpoLocations);
  const [syncing, setSyncing] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);
  const [syncingData, setSyncingData] = useState(false);
  const [syncDataSuccessMessage, setSyncDataSuccessMessage] = useState<string | null>(null);
  // const [syncMode, setSyncMode] = useState<'AUTOMATIC' | 'MANUAL'>('AUTOMATIC');
  const [selectedLocation, setSelectedLocation] = useState<OcpiLocation | null>(null);
  const hasSynced = useRef(false);

  // ── Data fetching ──────────────────────────────────────────────────────────

  /**
   * Synchronizes CPO locations from the CPO backend.
   */
  const handleSync = useCallback(async () => {
    setSyncing(true);
    hasSynced.current = true;
    try {
      await dispatch(syncCpoLocations({ countryCode, partyId })).unwrap();
    } catch {
      // Notification already dispatched by the syncCpoLocations thunk.
    } finally {
      setSyncing(false);
    }
  }, [countryCode, partyId, dispatch]);

  useEffect(() => {
    handleSync();
  }, [countryCode, partyId, handleSync]);

  /**
   * Broadcasts all CPO locations to connected partners.
   */
  const handleBroadcast = async () => {
    setBroadcasting(true);
    try {
      await dispatch(broadcastCpoLocations({ countryCode, partyId })).unwrap();
    } catch {
      // Notification already dispatched by the broadcastCpoLocations thunk.
    } finally {
      setBroadcasting(false);
    }
  };

  interface LocationRow {
    id: string;
    name: string;
    address: string;
    city: string;
    postalCode: string;
    partyId: string;
    countryCode: string;
    coordinates: string;
    parkingType: string;
    timeZone: string;
    open247: boolean;
    published: boolean;
    evsesCount: number;
    lastUpdated: string;
    _createdAt?: string;
    createdAt: string;
    raw: OcpiLocation;
  }

  const columns = [
    {
      label: t('ocpi:created'),
      accessorKey: '_createdAt' as keyof LocationRow,
      sortAllow: false,
      /**
       * Rendering created date
       * @param {LocationRow} currentRow The current location row.
       * @returns {any} The rendered created date element.
       */
      customData: (currentRow: LocationRow) => (
        <span>{ConvertUTCtoDateTime(currentRow._createdAt || currentRow.createdAt)}</span>
      ),
    },
    {
      label: t('ocpi:locationId'),
      accessorKey: 'id' as keyof LocationRow,
      sortAllow: false,
      columnWidth: 260,
      /**
       * Renders the location ID in a code badge.
       * @param {any} row The location row.
       */
      customData: (row: LocationRow) => <code className="cpo-location-id-badge">{row.id}</code>,
    },
    {
      label: t('ocpi:name'),
      accessorKey: 'name' as keyof LocationRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:address'),
      accessorKey: 'address' as keyof LocationRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:city'),
      accessorKey: 'city' as keyof LocationRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:postalCode'),
      accessorKey: 'postalCode' as keyof LocationRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:partyId'),
      accessorKey: 'partyId' as keyof LocationRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:countryCode'),
      accessorKey: 'countryCode' as keyof LocationRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:coordinates'),
      accessorKey: 'coordinates' as keyof LocationRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:parkingType'),
      accessorKey: 'parkingType' as keyof LocationRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:timeZone'),
      accessorKey: 'timeZone' as keyof LocationRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:open247'),
      accessorKey: 'open247' as keyof LocationRow,
      sortAllow: false,
      /**
       * Renders the 24/7 open status badge.
       * @param {LocationRow} row - The location row.
       * @returns {any} The status badge.
       */
      customData: (row: LocationRow) => (
        <span
          className={`cpo-published-badge ${
            row.open247 ? 'cpo-published-badge--yes' : 'cpo-published-badge--no'
          }`}>
          {row.open247 ? t('yes') : t('no')}
        </span>
      ),
    },
    {
      label: t('ocpi:published').toUpperCase(),
      accessorKey: 'published' as keyof LocationRow,
      sortAllow: false,
      /**
       * Renders the published status badge.
       * @param {LocationRow} row - The location row.
       * @returns {any} The status badge.
       */
      customData: (row: LocationRow) => (
        <span
          className={`cpo-published-badge ${
            row.published ? 'cpo-published-badge--yes' : 'cpo-published-badge--no'
          }`}>
          {row.published ? t('yes') : t('no')}
        </span>
      ),
    },
    {
      label: t('ocpi:evses').toUpperCase(),
      accessorKey: 'evsesCount' as keyof LocationRow,
      sortAllow: false,
      /**
       * Renders the EVSEs count badge.
       * @param {LocationRow} row - The location row.
       * @returns {any} The EVSEs count badge.
       */
      customData: (row: LocationRow) => (
        <span className="cpo-evses-count-badge">
          {row.evsesCount} {t(row.evsesCount !== 1 ? 'evseCountPlural' : 'evseCountSingular')}
        </span>
      ),
    },
    {
      label: t('ocpi:created'),
      accessorKey: 'createdAt' as keyof LocationRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:lastUpdated').toUpperCase(),
      accessorKey: 'lastUpdated' as keyof LocationRow,
      sortAllow: false,
    },
  ];

  const tableData: LocationRow[] = locations.map((loc: OcpiLocation) => {
    const dateString = loc.last_updated || loc._lastUpdated;
    const lastUpdated = dateString ? new Date(dateString).toLocaleDateString() : '–';
    const { coordinates } = loc;
    const createdAt =
      loc._createdAt || loc.createdAt || (loc as any).created_at || (loc as any).created || '';
    return {
      id: loc.id,
      name: loc.name || '–',
      address: loc.address || '–',
      city: loc.city || '–',
      postalCode: loc.postal_code || '–',
      partyId: loc.party_id || '–',
      countryCode: loc.country_code || '–',
      coordinates: coordinates ? `${coordinates.latitude}, ${coordinates.longitude}` : '–',
      parkingType: loc.parking_type || '–',
      timeZone: loc.time_zone || '–',
      open247: Boolean(loc.opening_times?.twentyfourseven),
      published: Boolean(loc.publish),
      evsesCount: loc.evses?.length ?? 0,
      lastUpdated,
      _createdAt: createdAt,
      createdAt,
      raw: loc,
    };
  });

  /**
   * Fetches CPO locations for this role with the given pagination/sort parameters.
   * @param {any} data - The pagination/sort parameters.
   */
  const fetchData = (data: ApiRequest) => {
    if (!loading) {
      dispatch(fetchCpoLocations({ countryCode, partyId, ...data }));
    }
  };

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageAndLimitChange = (value: ApiRequest) => {
    fetchData({ ...value, sortField: sortField ?? undefined, sortBy: sortBy ?? undefined });
  };

  // /**
  //  * Refreshes the current page of CPO locations.
  //  */
  // const handleRefresh = () => {
  //   fetchData({ page, limit, sortField, sortBy });
  // };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: { sortField: string; sortBy: 'asc' | 'desc' }) => {
    dispatch(setSortData(sortData));
    fetchData({
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  /**
   * Manually syncs charging station data with the OCPI gateway. Shows a success modal
   * and refreshes the locations list on success; errors (offline, no response, or a
   * server-side failure) are surfaced via the shared notification toast.
   * @returns {Promise<void>} Resolves when manual sync completes.
   */
  const handleSyncData = async (): Promise<void> => {
    setSyncingData(true);
    try {
      const response = await chargingLocationAPI.syncOcpiChargingStations();
      const rawMessage = response?.data?.message || response?.data?.status_message;
      let message = t('chargingStationDataSyncedSuccessfully');

      if (rawMessage) {
        if (rawMessage.toLowerCase().includes('started')) {
          message = t('chargingStationSyncStarted');
        } else if (rawMessage !== 'Charging station data synced successfully.') {
          message = rawMessage;
        }
      }

      setSyncDataSuccessMessage(message);
      handleRefresh();
    } catch (error: any) {
      dispatch(
        setNotificationMessage({ message: resolveSyncDataErrorMessage(error, t), type: 'error' }),
      );
    } finally {
      setSyncingData(false);
    }
  };

  return (
    <div className="cpo-locations-panel">
      {/* ── Top bar ── */}
      <div className="cpo-locations-topbar">
        <div>
          <h3 className="cpo-panel-title">{t('ocpiLocations')}</h3>
          <p className="cpo-panel-subtitle">
            {locations.length > 0
              ? t('locationsInGateway', { count: locations.length })
              : t('syncToLoadCpoLocations')}
          </p>
        </div>

        {/* Sync mode toggle */}
        {/* <div className="cpo-syncmode-toggle">
          <span className="cpo-syncmode-label">Push Mode:</span>
          <TabMenus
            openTab={syncModeTabs.indexOf(syncMode)}
            setOpenTab={(index) => setSyncMode(syncModeTabs[index])}
            menus={syncModeTabs.map((mode) => ({
              name: mode,
              icon: mode === 'AUTOMATIC' ? <Wifi size={12} /> : <WifiOff size={12} />,
              activeIcon: mode === 'AUTOMATIC' ? <Wifi size={12} /> : <WifiOff size={12} />,
            }))}
          />
        </div> */}
      </div>

      {/* ── Locations Table ── */}
      <CustomTable
        version="v2"
        header={columns}
        data={tableData}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading || syncing}
        serialNo
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageAndLimitChange}
        filters={filters}
        filtersToDispatch={updateCPOLocationsFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchCpoLocationFilters}
        sizeChangeButtonRequired
        actionDropdown
        rowClickEvent={(_id, row) => setSelectedLocation(row.raw)}
        rowClickParamsKey="id"
        refershCurrentPages={handleRefresh}
        requiredRefreshToggle
        refreshFunction={handleRefresh}
        refreshRequired
        handleRefresh={handleRefresh}
        hoverLabel={t('refresh')}
        rightTopContent={
          // syncMode === 'AUTOMATIC' ? (
          <div className="cpo-locations-actions">
            <MyButton
              label={syncingData ? t('syncing') : t('syncData')}
              svgIcon={<Sync />}
              buttonType="outline"
              onClickFunc={handleSyncData}
              disableButton={syncingData}
            />
            <MyButton
              buttonType="primary"
              label={broadcasting ? t('broadcasting') : t('broadcastAll')}
              svgIcon={<Upload />}
              onClickFunc={handleBroadcast}
              disableButton={broadcasting || locations.length === 0}
            />
          </div>
          // ) : undefined
        }
      />

      {/* ── Drill-down modal ── */}
      {selectedLocation && (
        <LocationDrillDownModal
          location={selectedLocation}
          onClose={() => setSelectedLocation(null)}
        />
      )}

      {/* ── Sync data success modal ── */}
      <ModalBox
        open={Boolean(syncDataSuccessMessage)}
        header={false}
        notification
        title={null}
        closeModal={() => setSyncDataSuccessMessage(null)}
        content={
          <SuccessNotification
            title={t('chargingStationDataSynced')}
            description={syncDataSuccessMessage || ''}
            onNotificationClose={() => setSyncDataSuccessMessage(null)}
          />
        }
      />
    </div>
  );
}
